import { Button, Paper, Stack, Tooltip } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { MaterialReactTable, MRT_RowData, MRT_TableInstance } from 'material-react-table'
import { ReactNode, useMemo } from 'react'
import { useTomTableStatePersistence } from './hooks/use-tom-table-state-persistence'

type Props<TData extends MRT_RowData> = {
  table: MRT_TableInstance<TData>
  tableName?: string
  keepPaginationState?: boolean
  keepColumnFiltersState?: boolean
  keepColumnSortingState?: boolean
}

type TomTableMeta<TData extends MRT_RowData> = {
  hasExternalTopToolbarCustomActions?: boolean
  renderTopToolbarLeftCustomActions?: (table: MRT_TableInstance<TData>) => ReactNode
}

export const TomTable = <TData extends MRT_RowData>(props: Props<TData>) => {
  const {
    table,
    keepPaginationState = true,
    keepColumnFiltersState = true,
    keepColumnSortingState = true,
    tableName,
  } = props
  const { t } = useTranslate()
  const { hasPersistedTableState, onResetTableState } = useTomTableStatePersistence({
    table,
    keepPaginationState,
    keepColumnFiltersState,
    keepColumnSortingState,
    tableName,
  })
  const tableMeta = (table.options.meta as TomTableMeta<TData> | undefined) ?? {}
  const hasExternalTopToolbarCustomActions = !!tableMeta.hasExternalTopToolbarCustomActions
  const resetTableButton = useMemo(
    () => (
      <Tooltip
        slotProps={{
          tooltip: {
            sx: {
              fontSize: '0.9rem',
              maxWidth: 420,
              lineHeight: 1.5,
              p: 1.5,
            },
          },
        }}
        title={t(
          'tableStateAppliedTooltip',
          'This table remembers your filters, sorting, and page in the current tab. Reset to see full data.',
        )}
      >
        <Button variant='text' size='small' onClick={onResetTableState}>
          {t('resetTable', 'Reset table')}
        </Button>
      </Tooltip>
    ),
    [onResetTableState, t],
  )

  if (hasExternalTopToolbarCustomActions) {
    const nextMeta = {
      ...(table.options.meta as TomTableMeta<TData> | undefined),
      renderTopToolbarLeftCustomActions: () =>
        hasPersistedTableState ? resetTableButton : undefined,
    }
    table.options.meta = nextMeta as any
  }

  return (
    <Paper>
      {hasPersistedTableState && !hasExternalTopToolbarCustomActions && (
        <Stack direction='row' alignItems='center' justifyContent='flex-start' px={2} py={1}>
          {resetTableButton}
        </Stack>
      )}
      <MaterialReactTable {...props} />
    </Paper>
  )
}
